import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Bot,
  Brain,
  Briefcase,
  Calendar,
  Camera,
  Gamepad2,
  GraduationCap,
  Laptop,
  MessageCircle,
  PenLine,
  Target,
} from "lucide-react";
import Reveal from "../components/ui/Reveal";
import PhoneMockup from "../components/ui/PhoneMockup";
import IconBox from "../components/ui/IconBox";
import SEO, { generateBreadcrumbSchema, generateFAQSchema } from "../components/seo/SEO";
import { useAppDownload } from "../context/AppDownloadContext";
import home1Screen from "../assets/images/home1.svg";
import aiAssistants from "../assets/images/ai-assistants.svg";
import jambDashboard from "../assets/images/jambdashboard.svg";

const preUniContent = {
  hero: {
    title: "Ace JAMB & WAEC.",
    subtitle: "Get into your dream university.",
    description: "CampusGenie gives you everything you need to prepare for and pass your entrance exams with confidence. Join thousands of students who scored high with our comprehensive prep tools.",
  },
  features: [
    {
      icon: BookOpen,
      title: "Past Questions Bank",
      description: "Access over 10 years of JAMB and WAEC past questions organized by subject, year, and topic. Practice with real exam questions and detailed explanations.",
      highlights: ["10+ years of questions", "All JAMB subjects", "63 WAEC subjects", "Detailed explanations"],
    },
    {
      icon: Laptop,
      title: "CBT Simulator",
      description: "Experience the exact JAMB CBT environment before your exam. Our simulator replicates the real interface, timing, and pressure so nothing surprises you on exam day.",
      highlights: ["Real JAMB interface", "Timed practice", "Score tracking", "Performance analysis"],
    },
    {
      icon: Bot,
      title: "AI Study Planner",
      description: "Get a personalized study schedule that adapts to your strengths and weaknesses. Our AI analyzes your performance and creates the optimal path to your target score.",
      highlights: ["Personalized schedules", "Weak area focus", "Daily tasks", "Progress tracking"],
    },
    {
      icon: Gamepad2,
      title: "Gamified Learning",
      description: "Stay motivated with daily challenges, study streaks, leaderboards, and achievements. Turn studying into an engaging daily habit that keeps you coming back.",
      highlights: ["Daily challenges", "Study streaks", "Leaderboards", "Achievement badges"],
    },
    {
      icon: Target,
      title: "Eligibility Checker",
      description: "Check your eligibility for any university and course based on your subject combination. Know your options before you finalize your choices.",
      highlights: ["All Nigerian universities", "Course requirements", "Subject matching", "JAMB score cutoffs"],
    },
    {
      icon: Brain,
      title: "AI Question Generator",
      description: "Never run out of practice questions. Our AI generates new questions based on syllabus topics, ensuring you're prepared for anything the exam throws at you.",
      highlights: ["Unlimited questions", "Syllabus-based", "Adaptive difficulty", "Instant feedback"],
    },
  ],
  stats: [
    { value: "298+", label: "Top scores achieved" },
    { value: "10+", label: "Years of past questions" },
    { value: "63", label: "WAEC subjects covered" },
    { value: "90%", label: "Recommend to friends" },
  ],
};

const uniContent = {
  hero: {
    title: "Thrive in university.",
    subtitle: "From admission to graduation.",
    description: "After you get in, CampusGenie becomes your complete university companion. Manage academics, connect with classmates, access AI tools, and discover opportunities - all in one app.",
  },
  features: [
    {
      icon: Calendar,
      title: "Study & Schedule Planner",
      description: "Manage your classes, assignments, study sessions, and personal tasks in one unified view. Sync with course rep updates so you never miss a schedule change.",
      highlights: ["Timetable view", "Assignment tracking", "Study blocks", "Automatic sync"],
    },
    {
      icon: MessageCircle,
      title: "Course Group Chats",
      description: "Connect with your coursemates in dedicated groups by course, year, or set. Course reps can send announcements that update everyone's timetable automatically.",
      highlights: ["Course-based groups", "Admin controls", "Timetable sync", "Announcements"],
    },
    {
      icon: PenLine,
      title: "AI Writing Assistant",
      description: "Access powerful AI tools for all your academic writing needs. Grammar check, paraphrase, translate, summarize, and more - all in one assistant.",
      highlights: ["Grammar checker", "Paraphraser", "Translator", "Summarizer"],
    },
    {
      icon: Camera,
      title: "AI Question Scanner",
      description: "Stuck on a problem? Snap a photo of any equation or question and get step-by-step explanations. Learn the process, not just the answer.",
      highlights: ["Photo capture", "Step-by-step", "Math support", "Explanations"],
    },
    {
      icon: GraduationCap,
      title: "Outline-Based Learning",
      description: "Upload your course outline and get auto-generated flashcards, quizzes, and summaries. Study exactly what you need for your exams.",
      highlights: ["Outline upload", "Auto flashcards", "Generated quizzes", "Topic summaries"],
    },
    {
      icon: Briefcase,
      title: "Career & Opportunities",
      description: "Discover internships, get AI career guidance, and access scholarship alerts. Start building your future while you're still in school.",
      highlights: ["Internship listings", "AI career bot", "Scholarships", "Application tracking"],
    },
  ],
  stats: [
    { value: "4-7", label: "Years of support" },
    { value: "8+", label: "AI assistant tools" },
    { value: "24/7", label: "AI availability" },
    { value: "100%", label: "Course coverage" },
  ],
};

export default function ForStudentsPage() {
  const { openAppModal } = useAppDownload();
  const [activeTab, setActiveTab] = useState("preuni");
  const content = activeTab === "preuni" ? preUniContent : uniContent;

  const faqs = [
    { question: "Is CampusGenie free for students?", answer: "CampusGenie offers a free tier with access to basic features. Premium features like higher AI limits and advanced analytics are available with a subscription." },
    { question: "Can I use CampusGenie for both JAMB and WAEC?", answer: "Yes! CampusGenie includes comprehensive past questions and CBT simulation for both JAMB and WAEC exams across all subjects." },
    { question: "Does CampusGenie work offline?", answer: "Many features work offline including downloaded past questions, study materials, and your saved notes. Sync happens automatically when you reconnect." },
    { question: "How does the university mode work?", answer: "After passing your exams, CampusGenie seamlessly transitions to university mode with features like course planning, timetables, internships, and campus life tools." },
  ];

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "For Students", url: "/students" },
  ];

  const structuredData = [
    generateBreadcrumbSchema(breadcrumbs),
    generateFAQSchema(faqs),
  ];

  return (
    <>
      <SEO
        title="For Students - Exam Prep & University Success Tools"
        description="CampusGenie helps students ace JAMB & WAEC exams and thrive in university. Access 10+ years of past questions, CBT simulation, AI study tools, academic planning, internships, and campus life support."
        url="/students"
        structuredData={structuredData}
      />

      {/* Hero */}
      <section className="section" style={{ paddingTop: "calc(var(--header-height) + 64px)" }}>
        <div className="container">
          <Reveal>
            <div className="section-heading section-heading-center">
              <span className="eyebrow">For Students</span>
              <h1 className="heading-hero" style={{ maxWidth: "18ch", margin: "0 auto 24px" }}>
                Built for every stage of your journey.
              </h1>
              <p className="body-large" style={{ maxWidth: "600px", margin: "0 auto" }}>
                Whether you're preparing for JAMB or navigating university life, CampusGenie has the tools you need to succeed.
              </p>
            </div>
          </Reveal>

          {/* Tab Navigation */}
          <Reveal delay={0.1}>
            <div className="students-tab-nav">
              <button
                type="button"
                className={`students-tab ${activeTab === "preuni" ? "active" : ""}`}
                onClick={() => setActiveTab("preuni")}
              >
                <Target size={18} strokeWidth={2} aria-hidden />
                Pre-University
              </button>
              <button
                type="button"
                className={`students-tab ${activeTab === "uni" ? "active" : ""}`}
                onClick={() => setActiveTab("uni")}
              >
                <GraduationCap size={18} strokeWidth={2} aria-hidden />
                University
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Dynamic Content Section */}
      <section className="section section-alt" style={{ paddingTop: 0 }}>
        <div className="container">
          <Reveal key={activeTab}>
            <div className="phase-card">
              <div className="phase-header">
                <div className="phase-icon">
                  <IconBox icon={activeTab === "preuni" ? Target : GraduationCap} size={36} />
                </div>
                <div>
                  <h2 className="heading-2" style={{ marginBottom: "8px" }}>
                    {content.hero.title}
                  </h2>
                  <p className="body-large" style={{ margin: 0 }}>
                    {content.hero.subtitle}
                  </p>
                </div>
              </div>
              <p className="body-base" style={{ maxWidth: "700px" }}>
                {content.hero.description}
              </p>
            </div>
          </Reveal>

          {/* Features Grid */}
          <div className="grid grid-2" style={{ marginTop: "48px" }}>
            {content.features.map((feature, index) => (
              <Reveal key={feature.title} delay={index * 0.05}>
                <div className="feature-card" style={{ height: "100%" }}>
                  <div style={{ marginBottom: "16px", color: "var(--color-primary)" }}>
                    <IconBox icon={feature.icon} size={40} />
                  </div>
                  <h3 className="heading-4" style={{ marginBottom: "12px" }}>{feature.title}</h3>
                  <p className="body-small" style={{ marginBottom: "20px" }}>{feature.description}</p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                    {feature.highlights.map((highlight) => (
                      <span key={highlight} className="badge badge-outline">
                        {highlight}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Stats */}
          <Reveal delay={0.2}>
            <div className="stats-grid" style={{ marginTop: "64px" }}>
              {content.stats.map((stat) => (
                <div key={stat.label} className="stat-card">
                  <div className="stat-value">{stat.value}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Visual Showcase */}
      <section className="section">
        <div className="container">
          <div className="showcase-grid">
            <Reveal>
              <div className="showcase-content">
                <span className="eyebrow">One App, Two Experiences</span>
                <h2 className="heading-2">
                  The same app grows with you.
                </h2>
                <p className="body-large">
                  When you gain admission, CampusGenie automatically transitions to university mode. No new app, no new account, no lost progress. Just continued support for your next chapter.
                </p>
                <div className="showcase-list">
                  <div className="showcase-list-item">Seamless transition from pre-uni to university mode</div>
                  <div className="showcase-list-item">Keep your account, history, and achievements</div>
                  <div className="showcase-list-item">Unlock new features designed for university life</div>
                  <div className="showcase-list-item">Access past JAMB prep tools for reference anytime</div>
                </div>
                <button type="button" className="btn btn-primary" style={{ marginTop: "24px" }} onClick={openAppModal}>
                  Download CampusGenie
                </button>
              </div>
            </Reveal>
            
            <Reveal delay={0.15}>
              <div className="students-dual-mockups">
                <div style={{ transform: "rotate(-5deg)" }}>
                  <PhoneMockup 
                    image={activeTab === "preuni" ? jambDashboard : home1Screen} 
                    alt="Pre-uni mode" 
                    size="default" 
                  />
                </div>
                <div style={{ transform: "rotate(5deg)" }}>
                  <PhoneMockup 
                    image={activeTab === "preuni" ? home1Screen : aiAssistants} 
                    alt="University mode" 
                    size="default" 
                  />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="section section-dark">
        <div className="container">
          <Reveal>
            <div className="section-heading section-heading-center">
              <span className="eyebrow">Success Stories</span>
              <h2 className="heading-2" style={{ color: "white" }}>
                Students who made it with CampusGenie.
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-3">
            <Reveal delay={0.1}>
              <div className="testimonial-card" style={{ 
                background: "rgba(255,255,255,0.05)", 
                borderColor: "rgba(255,255,255,0.1)" 
              }}>
                <p className="testimonial-quote" style={{ color: "rgba(255,255,255,0.9)" }}>
                  "I scored 298 in JAMB using CampusGenie. The CBT simulator made the real exam feel familiar."
                </p>
                <div className="testimonial-author">
                  <div className="testimonial-avatar">A</div>
                  <div>
                    <div className="testimonial-name" style={{ color: "white" }}>Adaeze N.</div>
                    <div className="testimonial-role" style={{ color: "rgba(255,255,255,0.6)" }}>Medicine, UNILAG</div>
                  </div>
                </div>
              </div>
            </Reveal>
            
            <Reveal delay={0.2}>
              <div className="testimonial-card" style={{ 
                background: "rgba(255,255,255,0.05)", 
                borderColor: "rgba(255,255,255,0.1)" 
              }}>
                <p className="testimonial-quote" style={{ color: "rgba(255,255,255,0.9)" }}>
                  "The study planner keeps me organized. I can see my timetable and assignments in one place."
                </p>
                <div className="testimonial-author">
                  <div className="testimonial-avatar">O</div>
                  <div>
                    <div className="testimonial-name" style={{ color: "white" }}>Oluwaseun T.</div>
                    <div className="testimonial-role" style={{ color: "rgba(255,255,255,0.6)" }}>Engineering, OAU</div>
                  </div>
                </div>
              </div>
            </Reveal>
            
            <Reveal delay={0.3}>
              <div className="testimonial-card" style={{ 
                background: "rgba(255,255,255,0.05)", 
                borderColor: "rgba(255,255,255,0.1)" 
              }}>
                <p className="testimonial-quote" style={{ color: "rgba(255,255,255,0.9)" }}>
                  "The AI assistant helped me improve my assignment writing. My grades went up significantly."
                </p>
                <div className="testimonial-author">
                  <div className="testimonial-avatar">F</div>
                  <div>
                    <div className="testimonial-name" style={{ color: "white" }}>Fatima M.</div>
                    <div className="testimonial-role" style={{ color: "rgba(255,255,255,0.6)" }}>Law, ABU Zaria</div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container">
          <Reveal>
            <div className="cta-section">
              <div className="cta-content">
                <h2 className="heading-2">
                  Start your success story today.
                </h2>
                <p className="body-large">
                  Join thousands of students using CampusGenie to achieve their academic goals.
                </p>
                <div className="cta-buttons">
                  <button type="button" className="btn btn-primary btn-lg" onClick={openAppModal}>
                    Download Free
                  </button>
                  <Link to="/product" className="btn btn-secondary btn-lg">
                    See All Features
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
